/**
 * Main app
 * 
 */

'use strict';

window.jQuery = window.$ = jQuery;

// External libs
import '@dotlottie/player-component';
import { Gradient } from './lib/gradient.js';
import { RDDL } from './lib/rddl';
import { PathFactoryLinkUTMs } from "./components/pathfactory-link-utms";

// Components
import { Nav } from './nav.js';
import { Hero } from './modules/m1-hero.js';
import { VerticalCarousel } from './modules/m3-vertical-carousel.js';
import { LogosGallery } from './modules/logos-gallery.js';
import { Resources } from './modules/m6-resources.js';
import { Faq } from './modules/m8-faq.js';
import { Tabs } from './modules/m12-tabs.js';
import { LogoText } from './modules/m15-logo-and-text.js';
import { Checklist } from './modules/m19-checklist.js';
import { CustomerCards } from './modules/m24-customer-cards.js';
import { Testimonial } from './modules/m23-testimonial-slider.js';
import { EventCards } from './modules/m36-event-cards.js';
import { FeatureCard } from './modules/m27-feature-card.js';
import { Leadership } from './modules/m47-leadership.js';
import { Timeline } from './modules/m48-timeline.js';
import { NewCards } from './modules/m51-new-cards.js';
import { Form } from './modules/m33-form.js';
import { Workplace } from './modules/m54-workplace-locations.js';
import { CompanyPerks } from './modules/m56-company-perks.js';
import { CompanyPositions } from './modules/m57-company-positions.js';
import { Board } from './modules/m48-board.js';
import { LearnCloudCards } from './modules/m61-learn-cloud-cards.js';

import { Search } from './search.js';
import { LearnCloud } from './templates/learn_cloud.js';

import { NewsletterCards } from './modules/m60-newsletter-cards.js';

import { A11yImgHelper } from './a11yDecorativeImgHelper.js';
import { A11yVideoHelper } from './a11yVideoHelper.js';

class App
{
  constructor()
  {
    this.nav = new Nav;

    this.init_rddl();
    this.smooth_scroll();
    this.init_gradients();
    this.init_globals();
    this.init_yt_videos();
    this.init_modal_keyboard_helpers();
    this.init_content_expire();

    // Post Copy Button
    this.postCopy = require('./gutenberg-blocks/post_button.js');

    // Anchor
    this.anchorBlock = require('./gutenberg-blocks/anchor.js');

    // Gutenberg dropdowns
    this.gutenbergeDropdowns = require('./gutenberg-blocks/dropdown.js');

    // M46 Dropdown
    this.gutenbergeDropdowns = require('./modules/m46-location.js');

    // Youtube
    this.youtubeLazyLoad = require('./components/yt-lazy-load.js');
    this.youtubeLazyLoad.init();

    this.hero              = new Hero;
    this.vertical_carousel = new VerticalCarousel;
    this.logos_gallery     = new LogosGallery;
    this.resources         = new Resources;
    this.faq               = new Faq;
    this.tabs              = new Tabs;
    this.logo_text         = new LogoText;
    this.checklist         = new Checklist;
    this.customer_cards    = new CustomerCards;
    this.testimonial       = new Testimonial;
    this.event_cards       = new EventCards;
    this.feature_card      = new FeatureCard;
    this.leadership        = new Leadership;
    this.timeline          = new Timeline;
    this.new_cards         = new NewCards;
    this.search            = new Search;
    this.form              = new Form;
    this.learn_cloud       = new LearnCloud;
    this.workplace         = new Workplace;
    this.companyPerks      = new CompanyPerks;
    this.companyPosition   = new CompanyPositions;
    this.board             = new Board;
    this.newsletter_cards  = new NewsletterCards;
    this.learn_cloud_cards = new LearnCloudCards;

    // A11y helpers
    this.a11y_img_helper = new A11yImgHelper;
    this.a11y_video_helper = new A11yVideoHelper;
  }

  /**
   * Smooth scrolling
   *
   * Used for Table of Contents on Single Blogs and Learn Cloud Native. These two are built a little different,
   * but this script is set up to work with both.
   */
  smooth_scroll() {
    const links = document.querySelectorAll('a[href^="#"]:not([data-toggle]):not(.search-link)');

    for (const link of links) {
      link.addEventListener('click', smooth_scroll_handler);
    }

    window.addEventListener('load', () => {
      let hash = window.location.hash;

      if ( hash == '' || hash == '#' || hash == undefined )
        return false;

      hash = hash.substring(1);

      const target = document.getElementById(hash);

      if (target)
        scroll_to(target);
    });

    function smooth_scroll_handler(e) {
      e.preventDefault();

      const href = this.getAttribute('href').substring(1);
      const target = document.getElementById(href);

      if (target)
        scroll_to(target);
    }

    /**
     * Calculate wrapper offsets and scroll to
     * 
     * @param string
     */
    function scroll_to(target) {
      let mobile            = window.matchMedia('(max-width: 1149.98px)');
      let offsetTop         = height_from_top(target);
      const navHeight       = document.querySelector('.c-site-nav-wrap')?.offsetHeight ?? 0;
      let tocHeight         = document.querySelector('.c-toc__wrapper')?.offsetHeight ?? 0;
      const mobileTocHeight = document.querySelector('.b-v4-block-container-anchors--mobile')?.offsetHeight ?? 0;
      let additionalOffset  = 10;
      let tabbedNavHeight   = document.querySelector('.c-v4-m25-tabbed-nav')?.offsetHeight ?? 0;

      /**
       * LCN single posts mobile toc is not sticky, the buttons are just stacked. If the media query is
       * true, then set the tocHeight to zero because the offsetTop calculation have already accounted for the toc.
       */
      if (mobile.matches) {
        tocHeight = 0;
      }

      /**
       * On single blogs, the table of contents dropdown opens is display block, then closes after clicking on a link.
       * This causes the offsetTop calculation to be larger than needed. We delete the height, then add 50 to account
       * for the dropdown select height.
       */
      if (mobileTocHeight) {
        offsetTop = Number(offsetTop) - Number(mobileTocHeight) + 50;
      }

      scroll({
        top: offsetTop - navHeight - tocHeight - additionalOffset - tabbedNavHeight,
        behavior: 'smooth'
      });
    }

    /**
     * Returns the total height of the element from the top of the window
     *
     * @param elem
     * @returns {number}
     */
    function height_from_top(elem) {
      let location = 0;
      if (elem.offsetParent) {
        do {
          location += elem.offsetTop;
          elem = elem.offsetParent;
        } while (elem);
      }
      return location >= 0 ? location : 0;
    }
  }

  /**
   * Update External go.sysdig.com links
   *
   */
  update_external_links() {
    this.pathfactoryLinkUTMs = new PathFactoryLinkUTMs;
    this.pathfactoryLinkUTMs.init();
  }
  
  /**
   * Initialize gradient canvas animations
   *
   * All canvas.gradient-canvas classes require an ID
   * 
   * @link https://whatamesh.vercel.app/
   */
  init_gradients()
  {
    $('.c-canvas--anim').each(function()
    {
      let _el      = $(this),
          gradient = new Gradient();
          gradient.initGradient( '#' + $('.gradient-canvas', this).attr('id') );

      $(window).on('load resize orientationchange', function()
      {
        $('img', _el)
          .css('maxWidth', $(window).width() / 2 )
          .addClass('ready');
      });
    });
  }

  /**
   * Init misc globals
   * 
   */
  init_globals()
  {
    $('a.c-card--wrapper, .c-v4-m12-tabs--main-item').hover(
      function()
      {
        $('.text-link', $(this)).addClass('hovered');
      },
      function()
      {
        $('.text-link', $(this)).removeClass('hovered');
      }
    );
  }

  /**
   * Init RDDL
   * 
   */
  init_rddl()
  {

    function getClientIdFromCookie() {
        const match = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
        return match ? match[1] : null;
    }
    // Wait for ga to be available
    ( async() => {
      while (!document.cookie.includes('_ga')) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      const clientId = getClientIdFromCookie();

        window.rddl = new RDDL;
        window.rddl.init({
          client_id: clientId,
          component_class:  '.o-section',
          footer_id:        'site-footer',
          form_class:       'sysdig-form',
          header_id:        'main-navigation',
          is_marketo:       true,
          //is_testing:       true,
          login_status:     false,
          main_body_id:     'content',
          munchkin_id:      '067-QZT-881',
          segment_id:       '',
          user_id:          '',
        });
    })();
  }

  /**
   * Init modal keyboard helpers
   *
   */
  init_modal_keyboard_helpers()
  {
    document.querySelectorAll('[data-toggle="modal"]').forEach(modal => {
      modal.addEventListener('keydown', e => {
        if ( e.key === 'Enter' ) {
          e.preventDefault();
          e.target.click();
        }
      });
    });
  }

  /**
   * Init YouTube videos
   *
   */
  init_yt_videos()
  {
    var tag     = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";

    var firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
   
    if ( window.sd_yt_videos && window.sd_yt_videos.length )
    {
      window.sd_yt_players = {};

      window.onYouTubeIframeAPIReady = function()
      {
        window.sd_yt_videos.forEach( ( video ) => {
          var current_video = new YT.Player( video.id, {
            videoId: video.video_id,
            events:  {
              'onStateChange': function( event )
              {
                if ( event.data && video.played == false )
                {
                  video.played = true;

                  window.rddl.track_youtube_video_start( video.id, YT, current_video );
                }
                else if ( event.data === 0 )
                {
                  window.rddl.track_youtube_video_end( video.id, YT, current_video );
                }
              }
            }
          });

          window.sd_yt_players[video.id] = current_video;
        });
      };
    }

    // If YT video in modal, pause it
    $('.modal.modal--video').on('hide.bs.modal', function ( event )
    {
      let video = $('iframe', $(this));

      if ( video.length )
      {
        window.sd_yt_players[video.attr('id')].pauseVideo();
      }
    });

    // Youtube player toggle
    $('[data-toggle="yt-player"]').on('click', function() {
      const yt_id = $(this).attr('data-target');

      // If YT player exists, play it
      if ( window.sd_yt_players[yt_id] ) {
        $(this).addClass('invisible');
        $(this).parent().removeClass('z-0');
        $('iframe#' + yt_id).removeClass('invisible');

        window.sd_yt_players[yt_id].playVideo();
      }
    });

    // Video player toggle
    $('[data-toggle="video-player"]').on('click', function() {
      const video_id = $(this).attr('data-target');

      $(this).addClass('invisible');
      $(this).parent().removeClass('z-0');

      $('video#' + video_id).removeClass('invisible')[0].play();
    });

    // Wistia player toggle
    $('[data-toggle="wistia-player"]').on('click', function () {
        const video_id = $(this).attr('data-target');

        $(this).addClass('invisible');
        $(this).parent().removeClass('z-0');

        $('div#' + video_id).removeClass('invisible');
        window._wq = window._wq || [];
        _wq.push({
            id: video_id, onReady: function (video) {
                video.play();
            }
        });
    });
  }

  /**
   * Initialize data-expire attribute to hide past content
   *
   * [data-attribute] value takes a Date() constructor value
   * 
   * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date
   */
  init_content_expire()
  {
    const current = new Date();
          current.toLocaleDateString('en-US', { timeZone: "America/Los_Angeles" });

    document.querySelectorAll('[data-expire]').forEach( el => {
      const d = new Date( el.getAttribute('data-expire') );
            d.toLocaleDateString('en-US', { timeZone: "America/Los_Angeles" });

      if ( d < current )
        el.classList.add('hidden');
    });
  }

  /**
   * Init
   * 
   */
  init()
  {
    this.nav.init();
    this.hero.init();
    this.vertical_carousel.init();
    this.logos_gallery.init();
    this.resources.init();
    this.faq.init();
    this.tabs.init();
    this.logo_text.init();
    this.checklist.init();
    this.customer_cards.init();
    this.testimonial.init();
    this.event_cards.init();
    this.feature_card.init();
    this.leadership.init();
    this.timeline.init();
    this.new_cards.init();
    this.form.init();
    this.workplace.init();
    this.companyPerks.init();
    this.companyPosition.init();
    this.board.init();
    this.search.init();
    this.learn_cloud.init();
    this.newsletter_cards.init();
    this.learn_cloud_cards.init();
    this.a11y_img_helper.init();
    this.a11y_video_helper.init();

    var entryContentImages = $('a > .img-lightbox');

    entryContentImages.on('click' , function( e )
    {
      e.preventDefault();

      $(this).parent().ekkoLightbox({
        alwaysShowClose: true,
      });
    })

    $('.c-carousel-quote-cards .slick-slider').each( function()
    {
      const id = $(this).attr('id');

      $(this).on('beforeChange', function( event, slick, currentSlide, nextSlide )
      {
        $('[data-slider=' + id + '] .slick-slider--pagination-count span').html( nextSlide + 1 );
      });

      $(this).slick({
        prevArrow: $('[data-slider=' + id + '] .slick-slider--pagination-prev'),
        nextArrow: $('[data-slider=' + id + '] .slick-slider--pagination-next'),
      });
    });

    $('.c-carousel-quote-cards-mini .slick-slider').each( function()
    {
      const id = $(this).attr('id');

      $(this).on('beforeChange', function( event, slick, currentSlide, nextSlide )
      {
        $('[data-slider=' + id + '] .slick-slider--pagination-count span').html( nextSlide + 1 );
      });

      $(this).slick({
        slidesToShow: 3,
        prevArrow: $('[data-slider=' + id + '] .slick-slider--pagination-prev'),
        nextArrow: $('[data-slider=' + id + '] .slick-slider--pagination-next'),
        responsive: [
        {
          breakpoint: 1150,
          settings: {
            slidesToShow: 1
          }
        }
        ]
      });
    });

    $('.c-gartner-quote-carousel .slick-slider').each(function () {
      const id = $(this).attr('id');

      $(this).slick({
        arrows: false,
        autoplay: true,
        autoplaySpeed: 4000
      });
    });
  
    $('.modal--video').on('show.bs.modal', function ( event ) {
      const thumbnail_video = $(this).parent().find('video');
      const video           = $(this).find('video');

      if ( thumbnail_video.length )
        thumbnail_video[0].pause();

      // Start video play on modal open
      if ( video.length )
        video[0].play();
    });

    $('.modal--video').on('hide.bs.modal', function ( event ) {
      const thumbnail_video = $(this).parent().find('video');
      const video           = $(this).find('video');

      if ( thumbnail_video.length )
        thumbnail_video[0].play();

      // Pause video on modal close
      if ( video.length )
        video[0].pause();
    });
  }
}

const app = new App;

app.init();
